import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Gift, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const DISMISS_KEY = "cultlyf_affiliate_pop_dismissed_at";
const DISMISS_WINDOW_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

// Routes where the pop is allowed to appear (storefront only)
const ALLOW_PREFIXES = ["/", "/shop", "/product", "/community"];
function pathAllowed(pathname) {
  if (pathname.startsWith("/admin") || pathname.startsWith("/login") || pathname.startsWith("/signup")) return false;
  if (pathname.startsWith("/checkout") || pathname.startsWith("/cart") || pathname.startsWith("/payment")) return false;
  if (pathname.startsWith("/account/affiliate")) return false;
  return ALLOW_PREFIXES.some((p) => (p === "/" ? pathname === "/" : pathname.startsWith(p)));
}

export default function AffiliatePop() {
  const [visible, setVisible] = useState(false);
  const [mounted, setMounted] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    // Show after a short delay so it feels intentional, not noisy
    if (!pathAllowed(location.pathname)) {
      setVisible(false);
      return;
    }
    const dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_WINDOW_MS) return;
    const t = setTimeout(() => { setVisible(true); setMounted(true); }, 1800);
    return () => clearTimeout(t);
  }, [location.pathname]);

  const close = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  const onClick = () => {
    if (user) {
      navigate("/account/affiliate");
    } else {
      navigate("/signup?redirect=/account/affiliate");
    }
  };

  if (!mounted || !visible) return null;

  return (
    <div
      className="fixed bottom-5 right-4 sm:right-6 z-[70] max-w-[340px] animate-[slideUp_.4s_ease-out]"
      data-testid="affiliate-pop"
    >
      <div className="relative bg-[#0A0A0A] border border-[#CFF048] shadow-[0_0_24px_-6px_rgba(207,240,72,0.5)] p-4 pr-9">
        <button
          onClick={close}
          aria-label="Close"
          className="absolute top-2 right-2 text-[#71717A] hover:text-white"
          data-testid="affiliate-pop-close"
        >
          <X size={14} />
        </button>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-[#CFF048] text-black flex items-center justify-center flex-shrink-0">
            <Gift size={20} />
          </div>
          <div className="min-w-0">
            <p className="label-mono text-[#CFF048]">// SELL & EARN</p>
            <p className="font-display font-black text-lg leading-tight mt-1">
              Earn <span className="text-[#CFF048]">₹100</span> per tee.
            </p>
            <p className="text-[#A1A1AA] text-xs mt-1 leading-relaxed">
              Drop your code on Insta, WhatsApp, X. Each tee sold = ₹100 in your pocket.
            </p>
            <button
              onClick={onClick}
              className="mt-3 w-full bg-[#CFF048] text-black font-mono font-bold text-xs px-3 py-2 hover:bg-white transition-colors"
              data-testid="affiliate-pop-cta"
            >
              {user ? "GET MY CODE →" : "JOIN THE CULT →"}
            </button>
          </div>
        </div>
      </div>
      <style>{`@keyframes slideUp { from { transform: translateY(20px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }`}</style>
    </div>
  );
}
